import { createElement, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"

import { normalizeLocale } from "../config"
import { RECAPTCHA_ENTERPRISE_SITEKEY } from "../const"
import { createTranslator } from "../i18n/translator"

export type VerificationCodeType = "email" | "phone"
export type VerificationCodeAction = "register" | "forgot" | "verify_account" | "bind_account"

export type VerificationCodeParams = {
  email?: string
  cellphone?: string
  channel?: "sms" | "whatsapp"
  authToken?: string
  verify_token?: string
}

export type VerificationCodeResult = { ok: true; data: unknown } | { ok: false; message: string }

export type UseVerificationCodeOptions = {
  type: VerificationCodeType
  action: VerificationCodeAction
  locale: string
  params: VerificationCodeParams
  requestKey?: string
  disabled?: boolean
  buttonClassName?: string
  showError: (message: string) => void
}

type Protocol<T> = { code: number; data: T | null; msg?: string }

type RecaptchaGlobal = {
  enterprise: {
    execute(siteKey: string, options: { action: string }): Promise<string>
  }
}

type VerificationHttp = {
  get<P, T>(url: string, config: unknown): Promise<Protocol<T> | null>
  post<P, T>(url: string, config: unknown): Promise<Protocol<T> | null>
}

const UNKNOWN_ERROR = "unknown error"

const toErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error || UNKNOWN_ERROR
  if (error instanceof Error) return error.message || UNKNOWN_ERROR
  return UNKNOWN_ERROR
}

const toFormData = (values: Record<string, string | undefined>): FormData => {
  const formData = new FormData()
  Object.entries(values).forEach(([key, value]) => {
    if (value) formData.append(key, value)
  })
  return formData
}

export const requestVerificationCode = async (
  options: Pick<UseVerificationCodeOptions, "type" | "action" | "locale" | "params">,
  client?: VerificationHttp
): Promise<VerificationCodeResult> => {
  const { type, action, locale, params } = options
  const t = createTranslator(normalizeLocale(locale))
  const requestClient = client ?? (await import("../core/http")).default
  const isEmail = type === "email"

  try {
    if (isEmail && (action === "register" || action === "forgot")) {
      const email = params.email?.trim() || ""
      const query = new URLSearchParams({ user_name: email })
      const existResponse = await requestClient.get<{ user_name: string }, { exist: boolean }>(
        `/usercenter/4.0/check_exist?${query}`,
        { showOriginResponse: true, getLang: () => locale }
      )
      if (!existResponse || existResponse.code !== 0) {
        return { ok: false, message: existResponse?.msg || UNKNOWN_ERROR }
      }

      const exists = Boolean(existResponse.data?.exist)
      if (action === "register" && exists) {
        return { ok: false, message: t("signInCommon.alreadyAccount") }
      }
      if (action === "forgot" && !exists) {
        return { ok: false, message: t("signInCommon.emailNotFound") }
      }

      const response = await requestClient.post<FormData, unknown>("/usercenter/english/v1/send_email_code", {
        body: toFormData({
          code_type: action === "register" ? "register_account" : "reset_account",
          email,
          source: "metal.multi",
          language: locale,
        }),
        showOriginResponse: true,
        getLang: () => locale,
      })
      return response?.code === 0
        ? { ok: true, data: response.data }
        : { ok: false, message: response?.msg || UNKNOWN_ERROR }
    }

    const path = isEmail ? "/usercenter/en/send_email_code" : "/usercenter/en/send_sms_code"
    const headers =
      action === "verify_account" || action === "bind_account" ? { "SMM-TOKEN": params.authToken || "" } : undefined
    const captcha = !isEmail
      ? await (globalThis as typeof globalThis & { grecaptcha: RecaptchaGlobal }).grecaptcha.enterprise.execute(
          RECAPTCHA_ENTERPRISE_SITEKEY,
          { action: "LOGIN" }
        )
      : undefined

    const response = await requestClient.post<FormData, unknown>(path, {
      body: toFormData({
        code_type: action === "register" ? "register_account" : action === "forgot" ? "reset_account" : action,
        email: isEmail ? params.email?.trim() : undefined,
        cellphone: !isEmail ? params.cellphone?.trim() : undefined,
        channel: !isEmail ? params.channel : undefined,
        captcha,
        verify_token: params.verify_token,
        language: locale,
      }),
      ...(headers ? { headers } : {}),
      showOriginResponse: true,
      getLang: () => locale,
    })
    return response?.code === 0
      ? { ok: true, data: response.data }
      : { ok: false, message: response?.msg || UNKNOWN_ERROR }
  } catch (error) {
    return { ok: false, message: toErrorMessage(error) }
  }
}

export const startVerificationCountdown = (
  onCountChange: (count: number) => void,
  setIntervalFn: typeof setInterval = setInterval,
  clearIntervalFn: typeof clearInterval = clearInterval
): (() => void) => {
  let count = 60
  onCountChange(count)
  const timer = setIntervalFn(() => {
    count -= 1
    onCountChange(count)
    if (count === 0) clearIntervalFn(timer)
  }, 1000)
  return () => clearIntervalFn(timer)
}

export const isVerificationCodeDisabled = (disabled: boolean, loading: boolean, count: number): boolean =>
  disabled || loading || count > 0

export const getVerificationCodeButtonLabel = (
  count: number,
  hasSent: boolean,
  requestKeyMatches: boolean,
  sendLabel: string,
  resendLabel: string
): string => {
  if (count > 0) return `${resendLabel}(${count}s)`
  return hasSent && requestKeyMatches ? resendLabel : sendLabel
}

export const completeVerificationCodeRequest = (
  result: VerificationCodeResult,
  handlers: { onSuccess: () => void; onError: (message: string) => void }
): void => {
  if (result.ok) handlers.onSuccess()
  else handlers.onError(result.message)
}

export const useVerificationCode = ({
  type,
  action,
  locale,
  params,
  requestKey,
  disabled = false,
  buttonClassName,
  showError,
}: UseVerificationCodeOptions): {
  sendButton: ReactNode
  requestCode: () => Promise<VerificationCodeResult>
  reset: () => void
} => {
  const [loading, setLoading] = useState(false)
  const [count, setCount] = useState(0)
  const hasSentRef = useRef(false)
  const sentRequestKeyRef = useRef<string | undefined>(undefined)
  const loadingRef = useRef(false)
  const countRef = useRef(0)
  const mountedRef = useRef(true)
  const clearCountdownRef = useRef<(() => void) | null>(null)
  const t = useMemo(() => createTranslator(normalizeLocale(locale)), [locale])
  const sendLabel = t("signInCommon.send")
  const resendLabel = t("signInCommon.resend")

  const reset = useCallback(() => {
    hasSentRef.current = false
    sentRequestKeyRef.current = undefined
    clearCountdownRef.current?.()
    clearCountdownRef.current = null
    countRef.current = 0
    setCount(0)
  }, [])

  const requestCode = useCallback(async (): Promise<VerificationCodeResult> => {
    if (disabled || loadingRef.current || countRef.current > 0) {
      return { ok: false, message: "Verification code request is disabled" }
    }

    loadingRef.current = true
    setLoading(true)
    const result = await requestVerificationCode({
      type,
      action,
      locale,
      params,
    }).finally(() => {
      loadingRef.current = false
      if (mountedRef.current) setLoading(false)
    })
    if (!mountedRef.current) return result

    completeVerificationCodeRequest(result, {
      onError: showError,
      onSuccess: () => {
        hasSentRef.current = true
        sentRequestKeyRef.current = requestKey
        clearCountdownRef.current?.()
        clearCountdownRef.current = startVerificationCountdown((nextCount) => {
          countRef.current = nextCount
          setCount(nextCount)
        })
      },
    })
    return result
  }, [action, disabled, locale, params, requestKey, showError, type])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearCountdownRef.current?.()
      clearCountdownRef.current = null
    }
  }, [])

  const label = getVerificationCodeButtonLabel(
    count,
    hasSentRef.current,
    sentRequestKeyRef.current === requestKey,
    sendLabel,
    resendLabel
  )
  const sendButton = createElement(
    "button",
    {
      type: "button",
      className: `g-recaptcha ${buttonClassName}`,
      part: "send",
      dataSitekey: RECAPTCHA_ENTERPRISE_SITEKEY,
      disabled: isVerificationCodeDisabled(disabled, loading, count),
      onClick: () => void requestCode(),
    },
    label
  )

  return { sendButton, requestCode, reset }
}

export default useVerificationCode
